// Deja constancia de cada cierre de sesión por inactividad.
//
// Existe por una pregunta que no se podía contestar: "el deslogueo automático,
// está funcionando?". El servidor guarda UN token por persona, compartido entre
// todos sus aparatos, así que no sabe cuántas sesiones hay vivas ni cuáles se
// cerraron. Lo único que quedaba era inferirlo de los ingresos, y eso miente:
// un hueco de 40 min en la telemetría puede ser alguien leyendo una ficha con
// el mouse en la mano, no una sesión cerrada.
//
// Con esto la respuesta sale de un conteo: cuántas sesiones se cerraron solas,
// en qué aparato y por cuál de los tres caminos. Un `idle_logout` con
// `source: "cel"` sería un bug (o un user agent cambiado a mano), no una
// estadística. Un 0 no prueba lo contrario: ver `pareceTelefono`.
import { flushActivity, logActivity } from "./activity-log";
import {
  enRutaPublicaDeAuth,
  estaExento,
  sesionVencida,
  uaDeTelefono,
} from "./idle-session";
import { logout } from "./authentication-helpers";
import { borrarSesionLocal } from "./session-token";

/**
 * Señal cruda de "esto parece un teléfono", a propósito SIN reusar
 * `esEscritorio()`: es la contraparte que puede contradecir al criterio que
 * decide cerrar. Si los dos coincidieran siempre, la alarma no podría sonar
 * nunca. Desde #1211 NO es del todo independiente: los dos miran
 * `uaDeTelefono()`, así que comparten el punto ciego de los teléfonos cuyo
 * navegador no se declara teléfono.
 *
 * Mira el user agent sin exigir pantalla táctil, y por eso sigue pudiendo
 * contradecir a `esEscritorio()` (#1211): sin esto, un teléfono que declara puntero
 * fino quedaba marcado "pc" y el conteo de cierres en celular daba 0 aunque
 * ocurrieran. Hoy suena cuando cierra un aparato cuyo navegador se declara
 * teléfono sin pantalla táctil: un teléfono que reporte `maxTouchPoints` en 0, o
 * un PC con el user agent cambiado a mano.
 *
 * Lo que NO ve: los celulares que siguen cerrando por las trampas abiertas de
 * `idle-session.js` ("Sitio de escritorio", plegable abierto sin "Mobile"),
 * porque su user agent no dice teléfono. Esos cierres siguen saliendo "pc", así
 * que un 0 acá no prueba que no existan.
 *
 * La segunda rama (puntero solo grueso y pantalla chica) ya no se cumple nunca
 * junto con un cierre: `esEscritorio()` solo deja cerrar a un aparato sin puntero
 * fino si tiene pantalla de PC. Se deja como red si ese criterio cambia.
 */
const pareceTelefono = () => {
  try {
    if (uaDeTelefono()) return true;
    const soloGrueso =
      window.matchMedia("(any-pointer: coarse)").matches &&
      !window.matchMedia("(any-pointer: fine)").matches;
    const chica = Math.max(window.screen?.width || 0, window.screen?.height || 0) < 900;
    return soloGrueso && chica;
  } catch {
    return false;
  }
};
/**
 * @param {"timer"|"al_volver"|"arranque"|"aviso"} motivo — los tres caminos por
 *   los que se cierra, más el aviso previo:
 *   · `timer`    — los 30 min se cumplieron con la pestaña a la vista.
 *   · `al_volver`— la pestaña estaba de fondo (el navegador congela el reloj) y
 *                  el médico volvió cuando ya había vencido.
 *   · `arranque` — la última actividad guardada ya venció al decidir sin
 *                  temporizador: al abrir la app, al volver `/api/user/me/` o
 *                  al cumplirse el techo de 12 s de `cerrarSalvoQueSeaExento`.
 *                  El caso típico es el computador que se apagó sin cerrar.
 *   · `aviso`    — el cartel de "Sigues ahí?" del último minuto.
 *
 * Se manda en el acto, sin esperar el flush periódico: el paso siguiente es un
 * `logout()` que borra el token, y sin token el evento ya no se puede reportar.
 */
export const avisarCierrePorInactividad = (motivo) => {
  try {
    // El aviso NO es un cierre: si compartieran event_type, contar cierres
    // exigiria filtrar por meta y el primer conteo apurado saldria inflado.
    logActivity(motivo === "aviso" ? "idle_aviso" : "idle_logout", {
      module: "auth",
      meta: {
        kind: motivo,
        // El aparato se mide con una señal DISTINTA de la que decidió
        // cerrar. Usar `esEscritorio()` acá era un detector que solo podía
        // confirmar: es la misma función que habilita el cierre, evaluada en el
        // mismo tick, así que jamás iba a decir "cel" y la alarma era código
        // muerto. Preguntando por el user agent y el puntero crudo, delata un
        // teléfono cerrado SOLO si su navegador se declara teléfono: los de las
        // trampas abiertas ("Sitio de escritorio", plegable sin "Mobile") salen
        // "pc" igual. Ver el docstring de `pareceTelefono`.
        source: pareceTelefono() ? "cel" : "pc",
      },
    });
    flushActivity();
  } catch {
    /* la telemetría jamás bloquea un cierre de sesión */
  }
};

/**
 * Cierra la sesión vencida sin temporizador: lo usan el chequeo de arranque, la
 * revalidación que corre cuando vuelve `/api/user/me/` y el techo de 12 s.
 *
 * TRAMPA ABIERTA (#1211, anterior a ese arreglo): el cierre por reloj NO pasa
 * por acá. `IdleLogoutGuard` llama `logout()` directo y siempre redirige, así
 * que una pestaña de PC con sesión que quedó 30 min en `/nueva-clave` pierde el
 * enlace del correo. Leído en el código, no reproducido.
 *
 * Cerrar y redirigir son dos cosas distintas, y separarlas mal ya rompió el
 * flujo de recuperar clave dos veces. En las rutas públicas de autenticación la
 * sesión vencida **muere igual**, pero sin redirigir: el enlace del correo trae
 * el token en la URL y una redirección lo borra, dejándole al médico un correo
 * que parece roto. Con la decisión acá, quien pase por esta función no puede
 * olvidarse; el cierre por reloj de arriba es el que se olvidó.
 */
export const cerrarPorInactividad = (motivo) => {
  avisarCierrePorInactividad(motivo);
  if (enRutaPublicaDeAuth()) borrarSesionLocal();
  // #1809 (revisor v12, F1): el cierre al cargar conserva la marca del link de pago de esta misma carga.
  else logout(motivo === "arranque" ? { conservarMarcaPagar: true } : undefined);
};

// Cuánto se le espera al backend para que diga si el usuario es exento, antes
// de cerrar igual. Es la contracara del fix que dejó de tratar "todavía no sé"
// como "no es exento": sin este techo, un backend que no contesta —el firewall
// de hospital que descarta en silencio, que ya nos pasó— dejaba la sesión
// vencida viva indefinidamente en el puesto compartido. Antes se cerraba sin
// necesitar red, así que era una protección que perdíamos.
//
// 12 s: más que el arranque frío del backend medido (~4 s) y menos que la
// paciencia de alguien parado frente a un computador ajeno.
const ESPERA_POR_EL_BACKEND_MS = 12000;
let cierrePendiente = null;

/**
 * Cierra en cuanto se sepa que el usuario NO es exento, o a lo sumo en
 * `ESPERA_POR_EL_BACKEND_MS` si nadie contesta.
 *
 * Lo cancela `cancelarCierrePendiente()`, que llama el guard cuando
 * `/api/user/me/` confirma que sí es exento.
 */
export const cerrarSalvoQueSeaExento = (motivo) => {
  if (cierrePendiente) return;
  cierrePendiente = setTimeout(() => {
    cierrePendiente = null;
    // Se revisa de nuevo: en estos segundos pudo llegar `/me` y el guard pudo
    // haber cerrado ya, o el usuario pudo haber vuelto a trabajar.
    if (sesionVencida({ ignorarExencion: true }) && !estaExento()) {
      cerrarPorInactividad(motivo);
    }
  }, ESPERA_POR_EL_BACKEND_MS);
};

/** El backend dijo que es exento: la sesión se queda. */
export const cancelarCierrePendiente = () => {
  if (!cierrePendiente) return;
  clearTimeout(cierrePendiente);
  cierrePendiente = null;
};
